import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useOrder, useGetQuoteLink, useCreateDepositInvoice, useGetEinvoicePdf, useGetDepositPackageLink, useAppConfig } from '@saiens/shared/hooks';
import type { DepositInvoiceResult } from '@saiens/shared/types';
import { formatCurrency, formatDate, getSaleStateLabel } from '@saiens/shared/utils';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import {
  ArrowLeft,
  User,
  Calendar,
  MapPin,
  Link as LinkIcon,
  Banknote,
  ExternalLink,
  FileText,
  ClipboardCheck,
} from 'lucide-react';

export function SaleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const orderId = id ? parseInt(id, 10) : undefined;

  const { data: order, isLoading, error } = useOrder(orderId);
  const getQuoteLink = useGetQuoteLink();
  const createDepositMutation = useCreateDepositInvoice();
  const getEinvoicePdfMutation = useGetEinvoicePdf();
  const getDepositPackageLink = useGetDepositPackageLink();
  // 逐公司旗標：玉禮沒串 ezPay，訂金電子發票由會計於後台開立。
  // 這裡只影響「怎麼講」，不影響「能不能建訂金憑單」——真正的閘門在後端，
  // 前端讀不到旗標時 fail-closed 回 false，最壞情況是措辭保守，不會誤開票。
  const { data: appConfig } = useAppConfig();
  const depositEinvoiceEnabled = appConfig?.enable_deposit_einvoice ?? false;

  const [showDepositModal, setShowDepositModal] = useState(false);
  const [depositModalStep, setDepositModalStep] = useState<1 | 2>(1);
  const [depositPct, setDepositPct] = useState(50);
  const [einvoiceItemName, setEinvoiceItemName] = useState('石材工程預收款');
  const [depositResult, setDepositResult] = useState<DepositInvoiceResult | null>(null);

  if (isLoading) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="px-4 py-6">
        <Link to="/sale" className="flex items-center gap-1 text-primary">
          <ArrowLeft className="h-4 w-4" /> 返回列表
        </Link>
        <div className="mt-8 text-center text-red-500">載入失敗或訂單不存在</div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 pb-24">
      {/* Back button */}
      <Link to="/sale" className="mb-4 flex items-center gap-1 text-sm text-primary">
        <ArrowLeft className="h-4 w-4" /> 返回列表
      </Link>

      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold text-gray-900">{order.name}</h1>
          <StateTag state={order.state} />
        </div>
        <div className="mt-2">
          <span className="text-2xl font-bold text-primary">
            {formatCurrency(order.amount_total)}
          </span>
        </div>
      </div>

      {/* Info Cards */}
      <div className="space-y-4">
        {/* Customer Info */}
        <div className="rounded-xl bg-white p-4 shadow-sm">
          <h2 className="mb-3 font-semibold text-gray-900">客戶資訊</h2>
          <div className="space-y-2">
            {order.partner_name && (
              <InfoRow icon={<User />} label="客戶" value={order.partner_name} />
            )}
            {order.contact_name && (
              <InfoRow icon={<User />} label="聯絡人" value={order.contact_name} />
            )}
            {order.project_addr && (
              <InfoRow icon={<MapPin />} label="專案地址" value={order.project_addr} />
            )}
            <InfoRow icon={<Calendar />} label="訂單日期" value={formatDate(order.date_order)} />
          </div>
        </div>

        {/* Order Lines */}
        {order.order_lines && order.order_lines.length > 0 && (
          <div className="rounded-xl bg-white p-4 shadow-sm">
            <h2 className="mb-3 font-semibold text-gray-900">
              訂單明細 ({order.order_lines.length})
            </h2>
            <div className="space-y-3">
              {order.order_lines.slice(0, 10).map((line) => (
                <div
                  key={line.id}
                  className="flex items-center justify-between border-b border-gray-100 pb-2 last:border-0"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">
                      {line.product_name || line.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {line.saiens_color && `${line.saiens_color} • `}
                      {line.product_uom_qty} {line.product_uom}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-gray-900">
                      {formatCurrency(line.price_subtotal)}
                    </p>
                    <p className="text-xs text-gray-500">
                      單價 {formatCurrency(line.price_unit)}
                    </p>
                  </div>
                </div>
              ))}
              {order.order_lines.length > 10 && (
                <p className="text-center text-sm text-gray-500">
                  還有 {order.order_lines.length - 10} 項...
                </p>
              )}
            </div>
          </div>
        )}

        {/* Amount Summary */}
        <div className="rounded-xl bg-white p-4 shadow-sm">
          <h2 className="mb-3 font-semibold text-gray-900">金額摘要</h2>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-500">小計</span>
              <span>{formatCurrency(order.amount_untaxed)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">稅額</span>
              <span>{formatCurrency(order.amount_tax)}</span>
            </div>
            <div className="flex justify-between border-t pt-2 text-lg font-semibold">
              <span>總計</span>
              <span className="text-primary">{formatCurrency(order.amount_total)}</span>
            </div>
          </div>
        </div>

        {/* Commission Info */}
        {order.sale_commission_amount > 0 && (
          <div className="rounded-xl bg-white p-4 shadow-sm">
            <h2 className="mb-3 font-semibold text-gray-900">佣金資訊</h2>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-500">佣金基準</span>
                <span>{formatCurrency(order.sale_commission_amount)}</span>
              </div>
              {order.user_sale_commission > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-500">業務佣金</span>
                  <span>{formatCurrency(order.user_sale_commission)}</span>
                </div>
              )}
              {order.coach_sale_commission > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-500">輔導佣金</span>
                  <span>{formatCurrency(order.coach_sale_commission)}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Invoice Summary */}
        {order.invoices && order.invoices.length > 0 && (
          <div className="rounded-xl bg-white p-4 shadow-sm">
            <h2 className="mb-3 font-semibold text-gray-900">應收憑單</h2>
            <div className="space-y-2">
              {order.invoices.map((inv) => (
                <div key={inv.id} className="flex items-start justify-between gap-2 text-sm">
                  <div className="min-w-0">
                    <p className="font-medium text-gray-800">
                      {inv.name}
                      {inv.is_downpayment && (
                        <span className="ml-1 text-xs text-gray-400">（預收）</span>
                      )}
                    </p>
                    {inv.einvoice_number && (
                      <button
                        onClick={() =>
                          getEinvoicePdfMutation.mutate(orderId!, {
                            onSuccess: (data) => {
                              if (data.pdf_url) window.open(data.pdf_url, '_blank');
                            },
                          })
                        }
                        disabled={getEinvoicePdfMutation.isPending}
                        className="mt-0.5 inline-flex items-center gap-0.5 text-xs text-blue-600 underline disabled:opacity-50"
                      >
                        <ExternalLink className="h-3 w-3" />
                        {inv.einvoice_number}
                      </button>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-medium">{formatCurrency(inv.amount_total)}</p>
                    <p className={`text-xs ${inv.state === 'posted' ? 'text-green-600' : 'text-amber-500'}`}>
                      {inv.state === 'posted' ? '已過帳' : '草稿'}
                    </p>
                  </div>
                </div>
              ))}
              <div className="border-t pt-2 flex justify-between text-sm font-medium text-gray-700">
                <span>已開應收合計</span>
                <span>{formatCurrency(order.invoices.reduce((s, i) => s + i.amount_total, 0))}</span>
              </div>
            </div>
          </div>
        )}

        {/* Signature Status */}
        {order.is_signed && (
          <div className="rounded-xl bg-green-50 p-4 border border-green-200">
            <div className="flex items-center gap-2">
              <span className="text-green-600 font-medium">客戶已簽署</span>
            </div>
            <p className="mt-1 text-sm text-green-700">
              簽署人: {order.signed_by_name} • {formatDate(order.signed_date)}
            </p>
          </div>
        )}

        {/* Deposit Invoice Result */}
        {depositResult && (
          <div className="rounded-xl bg-blue-50 p-4 border border-blue-200">
            <h2 className="mb-2 font-semibold text-blue-900">預收款發票已建立</h2>
            <div className="space-y-1 text-sm text-blue-800">
              <p>發票：{depositResult.invoice_name} ({depositResult.invoice_state})</p>
              <p>金額：{formatCurrency(depositResult.amount_total)}</p>
              {depositResult.einvoice_number && (
                <p className="flex items-center gap-1">
                  電子發票號碼：
                  <button
                    onClick={() =>
                      getEinvoicePdfMutation.mutate(orderId!, {
                        onSuccess: (data) => {
                          if (data.pdf_url) window.open(data.pdf_url, '_blank');
                        },
                      })
                    }
                    disabled={getEinvoicePdfMutation.isPending}
                    className="inline-flex items-center gap-0.5 font-medium text-blue-700 underline disabled:opacity-50"
                  >
                    {depositResult.einvoice_number}
                    <ExternalLink className="h-3 w-3" />
                  </button>
                </p>
              )}
              {depositResult.einvoice_error && (
                <p className="mt-1 text-amber-700">
                  ⚠ 電子發票開立失敗：{depositResult.einvoice_error}，請聯繫會計
                </p>
              )}
              {/* 依公司政策刻意不開票（例如玉禮）：中性灰、非警示，但一定要顯示，
                  否則業務會誤以為電子發票已經開出去了。 */}
              {!depositResult.einvoice_error && depositResult.einvoice_skipped_reason && (
                <p className="mt-1 text-slate-600">
                  ℹ 未開立電子發票：{depositResult.einvoice_skipped_reason}
                </p>
              )}
            </div>
            <button
              onClick={() => orderId && getDepositPackageLink.mutate(orderId)}
              disabled={getDepositPackageLink.isPending}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg bg-blue-700 py-2.5 text-sm font-medium text-white disabled:opacity-50"
            >
              {getDepositPackageLink.isPending ? (
                <LoadingSpinner size="sm" />
              ) : (
                <>
                  <FileText className="h-4 w-4" />
                  產生請款連結
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Deposit Invoice Modal - Step 1: 預收比例 */}
      {showDepositModal && depositModalStep === 1 && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40">
          <div className="w-full max-w-md rounded-t-2xl bg-white p-6">
            <h2 className="mb-4 text-lg font-bold text-gray-900">建立預收款 (1/2)</h2>
            <div className="mb-4">
              <label className="mb-1 block text-sm font-medium text-gray-700">
                預收比例：{depositPct}%
              </label>
              <input
                type="range"
                min={1}
                max={100}
                value={depositPct}
                onChange={(e) => setDepositPct(Number(e.target.value))}
                className="w-full accent-primary"
              />
              <div className="mt-1 flex justify-between text-xs text-gray-400">
                <span>1%</span>
                <span>50%</span>
                <span>100%</span>
              </div>
            </div>
            <p className="mb-4 text-sm text-gray-500">
              預收金額：{formatCurrency(order.amount_total * depositPct / 100)}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDepositModal(false)}
                className="flex-1 rounded-xl border border-gray-200 py-3 font-medium text-gray-700"
              >
                取消
              </button>
              <button
                onClick={() => setDepositModalStep(2)}
                className="flex-1 rounded-xl bg-primary py-3 font-medium text-white"
              >
                下一步 →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deposit Invoice Modal - Step 2: 電子發票確認 */}
      {showDepositModal && depositModalStep === 2 && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40">
          <div className="w-full max-w-md rounded-t-2xl bg-white p-6">
            <h2 className="mb-4 text-lg font-bold text-gray-900">
              {depositEinvoiceEnabled ? '電子發票確認 (2/2)' : '訂金憑單確認 (2/2)'}
            </h2>
            {!depositEinvoiceEnabled && (
              <div className="mb-4 rounded-lg bg-slate-100 px-3 py-2 text-xs text-slate-600">
                ℹ 本公司電子發票由會計於後台開立，這一步只建立訂金憑單，不會開出電子發票。
              </div>
            )}
            <div className="mb-4 space-y-3">
              <div>
                <p className="mb-0.5 text-xs font-medium text-gray-500">買受人名稱</p>
                <p className="text-sm font-medium text-gray-900">{order.partner_name || '—'}</p>
              </div>
              <div>
                <p className="mb-0.5 text-xs font-medium text-gray-500">統一編號</p>
                <p className="text-sm font-medium text-gray-900">
                  {order.partner?.vat || '個人戶（無統編）'}
                </p>
              </div>
              {depositEinvoiceEnabled && (
                <div>
                  <label className="mb-0.5 block text-xs font-medium text-gray-500">
                    電子發票品項名稱
                  </label>
                  <input
                    type="text"
                    value={einvoiceItemName}
                    onChange={(e) => setEinvoiceItemName(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-primary focus:outline-none"
                    placeholder="石材工程預收款"
                  />
                </div>
              )}
              <div>
                <p className="mb-0.5 text-xs font-medium text-gray-500">
                  {depositEinvoiceEnabled ? '發票金額' : '訂金金額'}
                </p>
                <p className="text-sm font-semibold text-primary">
                  {formatCurrency(order.amount_total * depositPct / 100)}
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setDepositModalStep(1)}
                className="flex-1 rounded-xl border border-gray-200 py-3 font-medium text-gray-700"
              >
                ← 返回
              </button>
              <button
                onClick={() => {
                  if (!orderId) return;
                  createDepositMutation.mutate(
                    { orderId, percentage: depositPct, itemName: einvoiceItemName },
                    {
                      onSuccess: (result) => {
                        setDepositResult(result);
                        setShowDepositModal(false);
                        setDepositModalStep(1);
                      },
                    }
                  );
                }}
                disabled={createDepositMutation.isPending}
                className="flex-1 rounded-xl bg-primary py-3 font-medium text-white disabled:bg-gray-300"
              >
                {createDepositMutation.isPending ? <LoadingSpinner size="sm" /> : '確認建立'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="fixed bottom-20 left-0 right-0 space-y-2 bg-white border-t p-4">
        {/* Generate Quote Link (unsigned orders) */}
        {(order.state === 'draft' || order.state === 'sent') && !order.is_signed && (
          <button
            onClick={() => orderId && getQuoteLink.mutate(orderId)}
            disabled={getQuoteLink.isPending}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-3 font-medium text-white hover:bg-primary/90 disabled:bg-gray-300"
          >
            {getQuoteLink.isPending ? (
              <LoadingSpinner size="sm" />
            ) : (
              <>
                <LinkIcon className="h-4 w-4" />
                產生報價單連結
              </>
            )}
          </button>
        )}

        {/* Create Deposit Invoice (confirmed order, no invoice yet) */}
        {order.state === 'sale' && !depositResult && (
          <button
            onClick={() => { setDepositModalStep(1); setShowDepositModal(true); }}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-amber-500 py-3 font-medium text-white hover:bg-amber-600"
          >
            <Banknote className="h-4 w-4" />
            建立預收款
          </button>
        )}

        {/* 驗收入口：訂單已確認即可開始驗收（是否符合驗收條件由 AcceptanceFormPage 判斷） */}
        {(order.state === 'sale' || order.state === 'done') && (
          <Link
            to={`/acceptance/create/${order.id}`}
            className="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-full bg-primary py-3 font-medium text-white hover:bg-primary/90"
          >
            <ClipboardCheck className="h-4 w-4" />
            開始驗收
          </Link>
        )}

        {/* Generate Deposit Package Link (confirmed order with existing deposit invoice) */}
        {order.state === 'sale' && !depositResult && order.invoices?.some((i) => i.is_downpayment) && (
          <button
            onClick={() => orderId && getDepositPackageLink.mutate(orderId)}
            disabled={getDepositPackageLink.isPending}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 py-3 font-medium text-white hover:bg-blue-700 disabled:bg-gray-300"
          >
            {getDepositPackageLink.isPending ? (
              <LoadingSpinner size="sm" />
            ) : (
              <>
                <FileText className="h-4 w-4" />
                產生請款連結
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

function InfoRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center justify-between py-1">
      <div className="flex items-center gap-2 text-gray-400">
        <span className="h-4 w-4">{icon}</span>
        <span className="text-sm">{label}</span>
      </div>
      <span className="font-medium text-gray-900">{value}</span>
    </div>
  );
}

function StateTag({ state }: { state: string }) {
  const colors: Record<string, string> = {
    draft: 'bg-gray-100 text-gray-700',
    sent: 'bg-blue-100 text-blue-700',
    sale: 'bg-green-100 text-green-700',
    done: 'bg-purple-100 text-purple-700',
    cancel: 'bg-red-100 text-red-700',
  };

  return (
    <span className={`inline-block rounded-full px-3 py-1 text-sm font-medium ${colors[state] || colors.draft}`}>
      {getSaleStateLabel(state)}
    </span>
  );
}
